"use client";

import type { LocalizedHref } from "@workspace/routing";
import NextLink from "next/link";
import type React from "react";
import type { FC, ReactNode } from "react";
import useSiteInfo from "../../siteInfo/useSiteInfo";
import getParsedHref from "./helpers/getParsedHref";
import getParsedHrefLocalized from "./helpers/getParsedHrefLocalized";
import patchHrefLocale from "./helpers/patchHrefLocale";

type BaseProps = Omit<React.AnchorHTMLAttributes<HTMLAnchorElement>, "href"> & {
  children: ReactNode;
};

// Bundlers inline NODE_ENV; consumers compile this source without node types.
declare const process: { env: { NODE_ENV?: string } };

// isLocalized marks the href as already carrying its locale prefix (a route
// builder's output), so the component never re-localises it.
type Props = BaseProps &
  (
    | { isLocalized: true; href: LocalizedHref }
    | { isLocalized?: false; href: string }
  );

const warnDoubleLocale = (
  href: string,
  language: string,
  defaultLang: string,
) => {
  if (process.env.NODE_ENV === "production" || language === defaultLang) {
    return;
  }
  if (href === `/${language}` || href.startsWith(`/${language}/`)) {
    console.warn(
      `LinkComponent: href "${href}" already starts with "/${language}" and will be prefixed again; pass isLocalized for pre-localised hrefs`,
    );
  }
};

const LinkComponent: FC<Props> = ({
  isLocalized,
  href,
  children,
  ...props
}) => {
  const { defaultLang, isBridgeSite, language, domain } = useSiteInfo();

  if (isLocalized) {
    const parsed = getParsedHrefLocalized({ href, isBridgeSite, domain });
    if (parsed.isExternal) {
      return (
        <a href={parsed.href} {...props}>
          {children}
        </a>
      );
    }
    return (
      <NextLink href={parsed.href} prefetch={false} {...props}>
        {children}
      </NextLink>
    );
  }

  warnDoubleLocale(href, language, defaultLang);
  const sharedProps = { href, language, domain, defaultLang, isBridgeSite };

  if (isBridgeSite) {
    const { href: resolvedHref, isExternal } = getParsedHref(sharedProps);
    if (isExternal) {
      return (
        <a href={resolvedHref.toString()} {...props}>
          {children}
        </a>
      );
    }
    return (
      <NextLink
        href={patchHrefLocale({ ...sharedProps, href: resolvedHref })}
        prefetch={false}
        {...props}
      >
        {children}
      </NextLink>
    );
  }

  return (
    <NextLink href={patchHrefLocale(sharedProps)} prefetch={false} {...props}>
      {children}
    </NextLink>
  );
};

export default LinkComponent;
